'use client';

import { useCallback, useEffect, useState } from 'react';

import {
  isPushNotificationSupported,
  removeAdminPushToken,
  requestPushPermissionAndGetToken,
  saveAdminPushToken,
  saveClientPushTokenToTicket,
} from '../services/fcmService';

export type PushPermissionStatus =
  | 'default'
  | 'granted'
  | 'denied'
  | 'unsupported';

const ADMIN_PUSH_TOKEN_KEY = 'dr_admin_push_token';

export function usePushNotifications() {
  const [permission, setPermission] = useState<PushPermissionStatus>('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentToken, setCurrentToken] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const supported = isPushNotificationSupported();
    setIsSupported(supported);

    if (!supported) {
      setPermission('unsupported');

      return;
    }

    setPermission(Notification.permission as PushPermissionStatus);
    const savedToken = localStorage.getItem(ADMIN_PUSH_TOKEN_KEY);
    if (savedToken) {
      setCurrentToken(savedToken);
    }
  }, []);

  const subscribeClientTicket = useCallback(
    async (ticketId: string): Promise<boolean> => {
      if (!isSupported) {
        return false;
      }

      setIsLoading(true);
      try {
        const token = await requestPushPermissionAndGetToken();
        setPermission(Notification.permission as PushPermissionStatus);

        if (!token) {
          return false;
        }

        await saveClientPushTokenToTicket(ticketId, token);
        setCurrentToken(token);

        return true;
      } catch (err) {
        console.warn('Failed to subscribe client ticket to push:', err);

        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [isSupported]
  );

  const subscribeAdmin = useCallback(
    async (user: { uid: string; email?: string | null }): Promise<boolean> => {
      if (!isSupported) {
        return false;
      }

      setIsLoading(true);
      try {
        const token = await requestPushPermissionAndGetToken();
        setPermission(Notification.permission as PushPermissionStatus);

        if (!token) {
          return false;
        }

        await saveAdminPushToken(token, user);
        localStorage.setItem(ADMIN_PUSH_TOKEN_KEY, token);
        setCurrentToken(token);

        return true;
      } catch (err) {
        console.warn('Failed to subscribe admin to push:', err);

        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [isSupported]
  );

  const unsubscribeAdmin = useCallback(async (): Promise<boolean> => {
    const token = currentToken || localStorage.getItem(ADMIN_PUSH_TOKEN_KEY);
    if (!token) {
      return true;
    }

    setIsLoading(true);
    try {
      await removeAdminPushToken(token);
      localStorage.removeItem(ADMIN_PUSH_TOKEN_KEY);
      setCurrentToken(null);

      return true;
    } catch (err) {
      console.warn('Failed to unsubscribe admin from push:', err);

      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentToken]);

  return {
    permission,
    isSupported,
    isLoading,
    currentToken,
    isAdminSubscribed: Boolean(currentToken),
    subscribeClientTicket,
    subscribeAdmin,
    unsubscribeAdmin,
  };
}
